import React, { useMemo, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/constants';
import { TableColumnInterface } from '../../../interfaces/tableColumn.interface';
import useGetDeveloperTokens from '../../../apiHooks/queries/authTokens/useGetDeveloperTokens';
import { useDeleteDeveloperToken } from '../../../apiHooks/mutations/authTokens/tokenMutations';
import TokensTable from '../../../components/tables/tokensTable/tokensTable';
import ConfirmDialog from '../../../dialogs/confirmDialog';
import FilterIcon from '../../../assets/icons/filter-icon.svg';
import DateTimeCell from '../../../partials/dateTimeCell';
import IdTooltipCell from '../../../partials/idTooltipCell';

const SERVER_FETCH_ROWS = 5000;

const columns: TableColumnInterface[] = [
  { field: 'id', headerName: 'id', cellRenderer: (params: string) => <IdTooltipCell params={params} /> },
  { field: 'type', headerName: 'type' },
  { field: 'name', headerName: 'name' },
  { field: 'lastUsedAt', headerName: 'lastUsedAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'lastUsedIp', headerName: 'lastUsedIp' },
  { field: 'userAgent', headerName: 'userAgent' },
  { field: 'ipAddress', headerName: 'ipAddress' },
  { field: 'issuedAt', headerName: 'issuedAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'expiresAt', headerName: 'expiresAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'revokedAt', headerName: 'revokedAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'revokedReason', headerName: 'revokedReason' },
  { field: 'createdAt', headerName: 'createdAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
];

type DeveloperTokenFilter = 'all' | 'revoked' | 'expired';

const tokenFilterLabels: Record<DeveloperTokenFilter, string> = {
  all: 'tokenFilterAll',
  revoked: 'tokenFilterRevoked',
  expired: 'tokenFilterExpired',
};

const DeveloperTokensTab = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<string | number | null>(null);
  const [tokenFilter, setTokenFilter] = useState<DeveloperTokenFilter>('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const { mutateAsync: deleteDeveloperToken, isLoading: isDeleting } = useDeleteDeveloperToken();

  const [searchInput, setSearchInput] = useState({
    search: searchParams.get('search') || '',
  });

  const handleSearchChange = (search: string) => {
    setPage(0);
    setSearchInput({ search });
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(newRowsPerPage);
  };

  const handleFilterChange = (filter: DeveloperTokenFilter) => {
    setPage(0);
    setTokenFilter(filter);
    setFilterAnchorEl(null);
  };

  const handleDeleteClick = (token: any) => {
    setSelectedTokenId(token?.id ?? null);
    setIsDeleteDialogOpen(true);
  };

  const handleOnDelete = async () => {
    if (selectedTokenId == null) return;
    await deleteDeveloperToken(selectedTokenId);
    setIsDeleteDialogOpen(false);
    setSelectedTokenId(null);
    refetch();
  };

  const {
    data: tokensData,
    isError,
    isFetching,
    refetch,
  } = useGetDeveloperTokens(
    'TOKENS_DEVELOPER',
    {
      search: searchInput.search,
      rows: SERVER_FETCH_ROWS,
      page: 1,
    },
  );

  const filteredTokens = useMemo(() => {
    const rows = tokensData?.data ?? [];
    const now = Date.now();

    if (tokenFilter === 'revoked') {
      return rows.filter((token: any) => !!token?.revokedAt);
    }

    if (tokenFilter === 'expired') {
      return rows.filter((token: any) => {
        if (token?.revokedAt || !token?.expiresAt) return false;
        return new Date(token.expiresAt).getTime() < now;
      });
    }

    return rows;
  }, [tokenFilter, tokensData?.data]);

  const pagedTokens = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTokens.slice(start, start + rowsPerPage);
  }, [filteredTokens, page, rowsPerPage]);

  const filterLabel = tokenFilterLabels[tokenFilter];

  return (
    <>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        variant="warning"
        title="deleteToken"
        text="deleteTokenConfirm"
        submitButtonText="delete"
        isDisabled={isDeleting}
        onCloseDialog={() => {
          setIsDeleteDialogOpen(false);
          setSelectedTokenId(null);
        }}
        onSubmit={handleOnDelete}
      />
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            borderRadius: '10px',
          },
        }}
      >
        {(['all', 'revoked', 'expired'] as DeveloperTokenFilter[]).map((filter) => (
          <MenuItem
            key={filter}
            selected={tokenFilter === filter}
            sx={{
              borderRadius: '8px',
              px: 2,
              py: 1,
              color: '#0C485E',
              '&.Mui-selected': {
                color: '#0C485E',
              },
              '&.Mui-selected:hover': {
                color: '#0C485E',
              },
            }}
            onClick={() => handleFilterChange(filter)}
          >
            {t(tokenFilterLabels[filter])}
          </MenuItem>
        ))}
      </Menu>
      <TokensTable
        title="tokensDeveloper"
        columns={columns}
        rows={pagedTokens}
        total={filteredTokens.length}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={handleRowsPerPageChange}
        isError={isError}
        isFetching={isFetching}
        isDeleting={isDeleting}
        refetch={refetch}
        search={searchInput.search}
        setSearch={handleSearchChange}
        onDelete={handleDeleteClick}
        headerContent={(
          <button
            type="button"
            onClick={(event) => setFilterAnchorEl(event.currentTarget)}
            className="flex h-[42px] items-center gap-2 rounded-[8px] border-[1px] bg-white px-4 text-[14px] text-primary"
          >
            <img src={FilterIcon} alt="" />
            <span>{t(filterLabel)}</span>
          </button>
        )}
      />
    </>
  );
};

export default DeveloperTokensTab;
